import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import type { ScheduledEvent } from '../../event-details';
import { TicketList } from '../ticket-list';
import { useZendeskAgents, useZendeskFields, useZendeskTickets } from '../use-zendesk';
import { CreateTicketCard } from './create-ticket-card';
import { LiveChat } from './live-chat';
import { TicketsHeader } from './tickets-header';

const ChatSection = styled.div`
  margin-top: 22px;
`;

interface Props {
  scheduledEvent: ScheduledEvent;
}

/*
 * Comms tab: the client's Zendesk tickets, plus the form to file a new one.
 *
 * Agents and issue types are fetched HERE, not inside the create card. The
 * Zendesk hooks have no cache, so loading them in a card that mounts and
 * unmounts would refetch on every open and blank the selects each time.
 */
export const CommsTab = ({ scheduledEvent }: Props): ReactElement => {
  const eid = String(scheduledEvent.id);
  const cid = String(scheduledEvent.location?.user?.id ?? '');
  const organization = scheduledEvent.location?.user?.organization_name ?? '';

  const [showCreate, setShowCreate] = useState(false);

  const tickets = useZendeskTickets(eid, cid);
  const agents = useZendeskAgents();
  const fields = useZendeskFields();

  return (
    <div>
      <TicketsHeader
        organization={organization}
        onNew={(): void => {
          setShowCreate(true);
        }}
      />

      {showCreate && (
        <CreateTicketCard
          scheduledEvent={scheduledEvent}
          assignees={agents.data ?? []}
          issueTypes={fields.data ?? []}
          onClose={(): void => {
            setShowCreate(false);
          }}
          onCreated={(): void => {
            setShowCreate(false);
            // Bring the new ticket into the list below.
            tickets.reload();
          }}
        />
      )}

      <TicketList
        tickets={tickets.data?.tickets ?? []}
        solvedFallback={tickets.data?.solvedFallback ?? false}
        loading={tickets.loading}
      />

      <ChatSection>
        <LiveChat scheduledEvent={scheduledEvent} />
      </ChatSection>
    </div>
  );
};
